// Platform-neutral API client (uses only fetch), shared by the web app and a
// future React Native app. Web uses the session cookie + CSRF header; a native
// app would pass a bearer token instead.

export interface ClientConfig {
  baseUrl: string;
  getOrgId: () => string | null;
  getCsrf?: () => string | null;
  getBearer?: () => string | null;
  onUnauthenticated?: () => void;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface RequestOptions {
  idempotencyKey?: string;
  orgId?: string | null;
  signal?: AbortSignal;
}

export function newIdempotencyKey(prefix = 'k'): string {
  const rnd = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`;
}

export function createClient(cfg: ClientConfig) {
  async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const orgId = opts.orgId === undefined ? cfg.getOrgId() : opts.orgId;
    if (orgId) headers['X-Org-Id'] = orgId;
    const bearer = cfg.getBearer?.();
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    else if (method !== 'GET') {
      const csrf = cfg.getCsrf?.();
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    let payload: string | FormData | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetch(cfg.baseUrl + path, { method, headers, body: payload, credentials: 'include', signal: opts.signal });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Could not reach SuperIndent. Check the internet and press the same button again – repeating it will not save twice.');
    }
    if (res.status === 204) return undefined as T;
    const type = res.headers.get('content-type') ?? '';
    const data = (type.includes('application/json') ? await res.json().catch(() => null) : null) as { error?: { code?: string; message?: string; details?: unknown } } | null;
    if (!res.ok) {
      if (res.status === 401) cfg.onUnauthenticated?.();
      const err = data?.error ?? {};
      throw new ApiError(res.status, err.code ?? 'ERROR', err.message ?? 'Something went wrong. Please try again.', err.details);
    }
    return data as T;
  }

  return {
    get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, opts),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body ?? {}, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body ?? {}, opts),
    del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
    /** Download URL for files the browser opens itself (statements, CSV); auth goes by cookie. */
    url: (path: string) => cfg.baseUrl + path,
  };
}

export type ApiClient = ReturnType<typeof createClient>;
